 import { useState, useCallback } from "react";
 import { useForm } from "react-hook-form";
 import { zodResolver } from "@hookform/resolvers/zod";
 import { z } from "zod";
 import { MapPin, Loader2 } from "lucide-react";
 import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogHeader,
   DialogTitle,
   DialogTrigger,
 } from "@/components/ui/dialog";
 import {
   Form,
   FormControl,
   FormDescription,
   FormField,
   FormItem,
   FormLabel,
   FormMessage,
 } from "@/components/ui/form";
 import { Input } from "@/components/ui/input";
 import { Button } from "@/components/ui/button";
 import { Textarea } from "@/components/ui/textarea";
 import { useCreatePlaceSuggestion } from "@/hooks/usePlaceSuggestions";
 import { AddressAutocomplete } from "@/features/producer-editor/components/AddressAutocomplete";
 import { GeocodeCandidate } from "@/features/producer-editor/hooks/useAddressSearch";
 import { matchRegion } from "@/features/producer-editor/utils/matchRegion";
 
 const suggestionSchema = z.object({
   suggested_name: z.string().min(2, "Namn måste vara minst 2 tecken"),
   address_line1: z.string().optional(),
   postal_code: z.string().optional(),
   city: z.string().optional(),
   region: z.string().optional(),
   lat: z.number().nullable().optional(),
   lng: z.number().nullable().optional(),
   note: z.string().optional(),
 });
 
 type SuggestionFormData = z.infer<typeof suggestionSchema>;
 
 interface SuggestPlaceDialogProps {
   producerId: string;
   trigger?: React.ReactNode;
   disabled?: boolean;
 }
 
 export function SuggestPlaceDialog({
   producerId,
   trigger,
   disabled = false,
 }: SuggestPlaceDialogProps) {
   const [open, setOpen] = useState(false);
   const [addressInputValue, setAddressInputValue] = useState("");
   const createSuggestion = useCreatePlaceSuggestion();
 
   const form = useForm<SuggestionFormData>({
     resolver: zodResolver(suggestionSchema),
     defaultValues: {
       suggested_name: "",
       address_line1: "",
       postal_code: "",
       city: "",
       region: "",
       lat: null,
       lng: null,
       note: "",
     },
   });
 
   // Handle address candidate selection from autocomplete
   const handleAddressSelect = useCallback(
     (candidate: GeocodeCandidate) => {
       // Fill address fields
       form.setValue("address_line1", candidate.street || candidate.label.split(",")[0] || "");
       form.setValue("postal_code", candidate.postcode || "");
       form.setValue("city", candidate.city || "");
       form.setValue("lat", candidate.lat);
       form.setValue("lng", candidate.lng);
 
       // Map county to region (landskap) using the existing utility
       if (candidate.county) {
         const mapped = matchRegion(candidate.county);
         form.setValue("region", mapped || "");
       }
 
       // Update the input to show the selected street
       setAddressInputValue(candidate.street || candidate.label.split(",")[0] || "");
     },
     [form]
   );
 
   const onSubmit = async (data: SuggestionFormData) => {
     await createSuggestion.mutateAsync({
       producer_id: producerId,
       suggested_name: data.suggested_name,
       address_line1: data.address_line1 || null,
       postal_code: data.postal_code || null,
       city: data.city || null,
       region: data.region || null,
       lat: data.lat ?? null,
       lng: data.lng ?? null,
       note: data.note || null,
     });
 
     form.reset();
     setAddressInputValue("");
     setOpen(false);
   };
 
   const latValue = form.watch("lat");
   const lngValue = form.watch("lng");
 
   return (
     <Dialog open={open} onOpenChange={setOpen}>
       <DialogTrigger asChild>
         {trigger || (
           <Button variant="outline" disabled={disabled}>
             <MapPin className="h-4 w-4 mr-2" />
             Föreslå ny plats
           </Button>
         )}
       </DialogTrigger>
       <DialogContent className="sm:max-w-[500px]">
         <DialogHeader>
           <DialogTitle>Föreslå ny plats</DialogTitle>
           <DialogDescription>
             Saknas platsen du letar efter? Skicka ett förslag så granskar vi det.
           </DialogDescription>
         </DialogHeader>
 
         <Form {...form}>
           <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
             <FormField
               control={form.control}
               name="suggested_name"
               render={({ field }) => (
                 <FormItem>
                   <FormLabel>Platsens namn *</FormLabel>
                   <FormControl>
                     <Input placeholder="T.ex. Södermalms Saluhall" {...field} />
                   </FormControl>
                   <FormMessage />
                 </FormItem>
               )}
             />
 
             {/* Address autocomplete with geolookup */}
             <div className="space-y-1">
               <AddressAutocomplete
                 value={addressInputValue}
                 onChange={setAddressInputValue}
                 onSelect={handleAddressSelect}
                 placeholder="Sök adress..."
                 id="suggest-place-address"
               />
               <p className="text-xs text-muted-foreground">
                 Börja skriva för att söka. Vi fyller i koordinater automatiskt.
               </p>
             </div>
 
             {/* Show auto-filled fields */}
             <div className="grid grid-cols-2 gap-4">
               <FormField
                 control={form.control}
                 name="postal_code"
                 render={({ field }) => (
                   <FormItem>
                     <FormLabel>Postnummer</FormLabel>
                     <FormControl>
                       <Input placeholder="123 45" {...field} />
                     </FormControl>
                     <FormMessage />
                   </FormItem>
                 )}
               />
 
               <FormField
                 control={form.control}
                 name="city"
                 render={({ field }) => (
                   <FormItem>
                     <FormLabel>Ort</FormLabel>
                     <FormControl>
                       <Input placeholder="Stockholm" {...field} />
                     </FormControl>
                     <FormMessage />
                   </FormItem>
                 )}
               />
             </div>
 
             <FormField
               control={form.control}
               name="region"
               render={({ field }) => (
                 <FormItem>
                   <FormLabel>Landskap</FormLabel>
                   <FormControl>
                     <Input placeholder="Fylls i automatiskt" {...field} />
                   </FormControl>
                   <FormMessage />
                 </FormItem>
               )}
             />
 
             {/* Show lat/lng if available (read-only) */}
             {(latValue != null || lngValue != null) && (
               <div className="text-xs text-muted-foreground bg-muted/50 px-3 py-2 rounded-md">
                 📍 Koordinater: {latValue?.toFixed(5)}, {lngValue?.toFixed(5)}
               </div>
             )}
 
             <FormField
               control={form.control}
               name="note"
               render={({ field }) => (
                 <FormItem>
                   <FormLabel>Notering</FormLabel>
                   <FormControl>
                     <Textarea
                       placeholder="Beskriv platsen eller varför den bör finnas med..."
                       className="min-h-[80px]"
                       {...field}
                     />
                   </FormControl>
                   <FormDescription>
                     Valfri information som kan hjälpa oss verifiera platsen.
                   </FormDescription>
                   <FormMessage />
                 </FormItem>
               )}
             />
 
             <div className="flex justify-end gap-2 pt-4">
               <Button
                 type="button"
                 variant="outline"
                 onClick={() => setOpen(false)}
               >
                 Avbryt
               </Button>
               <Button type="submit" disabled={createSuggestion.isPending}>
                 {createSuggestion.isPending ? (
                   <>
                     <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                     Skickar...
                   </>
                 ) : (
                   "Skicka förslag"
                 )}
               </Button>
             </div>
           </form>
         </Form>
       </DialogContent>
     </Dialog>
   );
 }